import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Container from 'reactstrap/lib/Container';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { bindToContent, CmsEntities, CmsModel } from 'client/data/models/cms';
import { TrackingHandler } from 'client/tracking/handler';
import { TrackingMap } from 'client/tracking/maps/industry-center';
import {
  getPressArticleContentPath,
  getPressNewArticleContentPath,
  getPressLandingContentPath,
} from 'site-modules/editorial/utils/content-path';
import { getPressNewArticleBreadcrumbs } from 'site-modules/editorial/utils/breadcrumbs';
import { parseContent } from 'client/data/cms/content';
import { Breadcrumbs } from 'site-modules/shared/components/breadcrumbs/breadcrumbs';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { MediaContactsCard } from 'site-modules/editorial/components/press/media-contacts-card/media-contacts-card';

import './press-article.scss';

function PressArticleUI({ articleId, content = null, pressLandingContent = null, showBreadcrumbs = false }) {
  useEffect(() => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    TrackingHandler.useMap(TrackingMap);
  }, []);

  if (!content) {
    return null;
  }

  const { title, subtitle } = content;
  const { htmlContent } = content.child('html').getAllMetadata();
  const bodyContent = htmlContent || content.child('0').content || content.content;
  const breadcrumbs = getPressNewArticleBreadcrumbs({ articleId, title });
  const mediaContactsEntry = pressLandingContent && pressLandingContent.child('media-contacts');
  const mediaContactCards = mediaContactsEntry ? mediaContactsEntry.children() : [];

  return (
    <div className="press-article__bg">
      <Container
        tag="main"
        id="main-content"
        tabIndex={-1}
        className={classnames('press-article', showBreadcrumbs ? 'pt-1' : 'pt-3')}
      >
        {showBreadcrumbs && <Breadcrumbs className="ps-0 mb-1 mb-md-2 text-center" data={breadcrumbs} />}
        <Row className="align-items-start">
          <Col xs={12} lg={8} className={classnames({ 'pt-3': !showBreadcrumbs }, 'pb-1 pb-lg-3')}>
            <h1 className="press-article__title fw-bold text-gray-darker mb-0_75">{title}</h1>
            {!!subtitle && <p className="press-article__subtitle size-20 text-gray-darker mb-1_5">{subtitle}</p>}
            <ContentFragment classes="text-gray-darker">{bodyContent}</ContentFragment>
          </Col>
          {!!mediaContactCards.length && (
            <Col xs={12} lg={4} className={classnames({ 'pt-lg-3': !showBreadcrumbs }, 'ps-lg-3')}>
              <h2 className="size-20 fw-bold text-gray-darker mb-1_5">{mediaContactsEntry.title}</h2>
              <div className="d-flex flex-column gap-2">
                {mediaContactCards.map(card => (
                  <MediaContactsCard key={card.id} entry={card} fill={false} />
                ))}
              </div>
            </Col>
          )}
        </Row>
      </Container>
    </div>
  );
}

PressArticleUI.propTypes = {
  articleId: PropTypes.string.isRequired,
  content: CmsEntities.Content,
  pressLandingContent: CmsEntities.Content,
  showBreadcrumbs: PropTypes.bool,
};

function PressNewArticleUI(props) {
  return <PressArticleUI {...props} showBreadcrumbs />;
}

PressNewArticleUI.propTypes = PressArticleUI.propTypes;

export const PressArticle = connectToModel(PressArticleUI, {
  content: bindToPath(
    ({ articleId }) => `content["${getPressArticleContentPath(articleId)}.withContent"]`,
    CmsModel,
    parseContent
  ),
  pressLandingContent: bindToContent(() => getPressLandingContentPath()),
});

export const PressNewArticle = connectToModel(PressNewArticleUI, {
  content: bindToPath(
    ({ articleId }) => `content["${getPressNewArticleContentPath(articleId)}.withContent"]`,
    CmsModel,
    parseContent
  ),
  pressLandingContent: bindToContent(() => getPressLandingContentPath()),
});
